const QUEUE_CONTEXT_KEY = 'pickingQueueContext';
const ACTIVE_ORDER_KEY = 'activePickingOrderId';
const CONTEXT_TTL = 24 * 60 * 60 * 1000;

export const normalizePickingOrderId = (value) => String(value || '').trim();

export const getPickingOrderId = (order) => {
  if (!order) return '';
  return normalizePickingOrderId(order.increment_id ?? order.incrementId ?? order.orderId ?? order.entity_id);
};

export const readActivePickingOrderId = () => {
  if (typeof window === 'undefined') return '';
  try {
    return normalizePickingOrderId(window.localStorage.getItem(ACTIVE_ORDER_KEY));
  } catch {
    return '';
  }
};

export const saveActivePickingOrder = (orderOrId) => {
  if (typeof window === 'undefined') return;
  const id = typeof orderOrId === 'object'
    ? getPickingOrderId(orderOrId)
    : normalizePickingOrderId(orderOrId);
  if (!id) return;
  try {
    window.localStorage.setItem(ACTIVE_ORDER_KEY, id);
  } catch {
    // ignore storage failures
  }
};

export const readPickingQueueContext = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(QUEUE_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const completedId = normalizePickingOrderId(parsed?.completedId);
    const queueIds = Array.isArray(parsed?.queueIds)
      ? parsed.queueIds.map(normalizePickingOrderId).filter(Boolean)
      : [];
    const at = Number(parsed?.at) || 0;

    if (!completedId || !queueIds.length || (at && Date.now() - at > CONTEXT_TTL)) {
      window.localStorage.removeItem(QUEUE_CONTEXT_KEY);
      return null;
    }

    return { completedId, queueIds, at };
  } catch {
    try {
      window.localStorage.removeItem(QUEUE_CONTEXT_KEY);
    } catch {
      // ignore
    }
    return null;
  }
};

export const savePickingQueueContext = ({ currentOrder, orders = [] }) => {
  if (typeof window === 'undefined') return;
  const completedId = getPickingOrderId(currentOrder);
  const queueIds = orders.map(getPickingOrderId).filter(Boolean);
  if (!completedId || !queueIds.length) return;

  try {
    window.localStorage.setItem(QUEUE_CONTEXT_KEY, JSON.stringify({
      completedId,
      queueIds,
      at: Date.now(),
    }));
    saveActivePickingOrder(completedId);
  } catch {
    // ignore storage failures
  }
};

export const chooseNeighborAfterCompleted = (availableOrders = [], context = null) => {
  const safeContext = context || readPickingQueueContext();
  if (!safeContext?.completedId || !Array.isArray(safeContext.queueIds)) return '';

  const availableIds = new Set(availableOrders.map(getPickingOrderId).filter(Boolean));
  if (!availableIds.size) return '';

  const completedIndex = safeContext.queueIds.indexOf(safeContext.completedId);
  if (completedIndex === -1) return '';

  const candidates = [
    ...safeContext.queueIds.slice(completedIndex + 1),
    ...safeContext.queueIds.slice(0, completedIndex).reverse(),
  ];

  return candidates.find((id) => availableIds.has(id)) || '';
};

export const choosePreferredPickingOrder = (
  availableOrders = [],
  { preferredId = '', activeId = '', context = null } = {},
) => {
  const availableIds = new Set(availableOrders.map(getPickingOrderId).filter(Boolean));
  const normalizedPreferred = normalizePickingOrderId(preferredId);
  const normalizedActive = normalizePickingOrderId(activeId);

  if (normalizedPreferred && availableIds.has(normalizedPreferred)) {
    return normalizedPreferred;
  }

  const neighborId = chooseNeighborAfterCompleted(availableOrders, context);
  if (neighborId) return neighborId;

  if (normalizedActive && availableIds.has(normalizedActive)) {
    return normalizedActive;
  }

  return '';
};
